/**
 * バックエンド (functions/api/**) 呼び出しの一元化レイヤー。
 * docs/API.md の契約に対して型付けする。すべて credentials: "include" 付き。
 */
import type {
  AdminConfig,
  AdminProgressResponse,
  EvaluationDetail,
  EvaluationsListResponse,
  NotifyExamineeResponse,
  PublicConfig,
  RemindPreviewResponse,
  RemindSendResponse,
  SaveEvaluationBody,
  SaveEvaluationResponse,
  SessionResponse,
} from "./types";

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

type JsonRecord = Record<string, unknown>;

function isJsonRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null;
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "include",
      headers: init?.body ? { "Content-Type": "application/json" } : undefined,
      ...init,
    });
  } catch {
    return { ok: false, error: "サーバーに接続できませんでした" };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  if (!res.ok) {
    const message =
      isJsonRecord(json) && typeof json.error === "string"
        ? json.error
        : `リクエストに失敗しました (${res.status})`;
    return { ok: false, error: message };
  }

  if (isJsonRecord(json) && json.ok === false) {
    const message = typeof json.error === "string" ? json.error : "リクエストに失敗しました";
    return { ok: false, error: message };
  }

  return { ok: true, data: json as T };
}

function postJson<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  return request<T>(path, { method: "POST", body: JSON.stringify(body) });
}

function post<T>(path: string): Promise<ApiResult<T>> {
  return request<T>(path, { method: "POST" });
}

// ---- 認証 / セッション ----

export function getSession(): Promise<ApiResult<SessionResponse>> {
  return request<SessionResponse>("/api/session");
}

export type LoginResult = {
  name: string;
  admin: boolean;
  evaluator: string | null;
  mustChangePassword: boolean;
};

export function login(username: string, password: string): Promise<ApiResult<LoginResult>> {
  return postJson<LoginResult>("/api/auth/login", { username, password });
}

export function logout(): Promise<ApiResult<Record<string, never>>> {
  return post<Record<string, never>>("/api/auth/logout");
}

export function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<ApiResult<Record<string, never>>> {
  return postJson<Record<string, never>>("/api/auth/change-password", { currentPassword, newPassword });
}

// ---- 設定 ----

export function getConfig(): Promise<ApiResult<PublicConfig>> {
  return request<PublicConfig>("/api/config");
}

export function getAdminConfig(): Promise<ApiResult<AdminConfig>> {
  return request<AdminConfig>("/api/admin/config");
}

export function updateAdminConfig(patch: Partial<AdminConfig>): Promise<ApiResult<Record<string, never>>> {
  return postJson<Record<string, never>>("/api/admin/config", patch);
}

// ---- 評価者向け ----

export function getEvaluations(): Promise<ApiResult<EvaluationsListResponse>> {
  return request<EvaluationsListResponse>("/api/evaluations");
}

export function getEvaluation(rowId: number): Promise<ApiResult<EvaluationDetail>> {
  return request<EvaluationDetail>(`/api/evaluations/${rowId}`);
}

export function saveEvaluation(
  rowId: number,
  body: SaveEvaluationBody
): Promise<ApiResult<SaveEvaluationResponse>> {
  return postJson<SaveEvaluationResponse>(`/api/evaluations/${rowId}/save`, body);
}

// ---- 管理者向け ----

export function getAdminProgress(): Promise<ApiResult<AdminProgressResponse>> {
  return request<AdminProgressResponse>("/api/admin/progress");
}

export function remindPreview(): Promise<ApiResult<RemindPreviewResponse>> {
  return post<RemindPreviewResponse>("/api/admin/remind/preview");
}

export function remindSend(): Promise<ApiResult<RemindSendResponse>> {
  return post<RemindSendResponse>("/api/admin/remind/send");
}

export function notifyExaminee(
  examinerId: string,
  message: string
): Promise<ApiResult<NotifyExamineeResponse>> {
  return postJson<NotifyExamineeResponse>("/api/admin/notify-examinee", { examinerId, message });
}
